import { ticketsCollection, ticketsDownloadsCollection } from "@/config/firebase";
import { roles } from "@/helpers/get_constants";
import getCurrentUser from "@/helpers/get_current_user";
import requestAxios from "@/helpers/request_axios";
import * as MutationsType from "./mutationsType";

export default {
  // eslint-disable-next-line
  async createTicket({ commit }, { ticket }) {
    try {
      let response = await requestAxios({
        url: "/awy/api/v1/ticket",
        method: "POST",
        data: {
          ...ticket,
          origin: ticket.origin || "agent",
          from_apolocrm: true,
        },
      });
      return response.data;
    } catch (error) {
      throw error.response.data;
    }
  },
  async resetTicketReport({ commit }) {
    commit(MutationsType.REMOVE_TICKETS_BY_STATE);
  },
  async setTicketsReport({ state, commit }, { filters, itemsPerPage }) {
    try {
      let response = await requestAxios({
        url: "/awy/api/v1/ticket/report",
        method: "GET",
        params: { filters, itemsPerPage },
      });
      let data = response.data.data;
      let tickets = data.tickets;

      if (state.ticketsByState[filters.state]) {
        tickets = [...state.ticketsByState[filters.state].tickets, ...tickets];
      }

      data.tickets = tickets;

      commit(MutationsType.SET_TICKETS_BY_STATE, { state_id: filters.state, ...data });
      commit(MutationsType.SET_TICKETS, tickets);
    } catch (error) {
      throw error.response.data;
    }
  },
  async updateTicketsReport({ commit }, { stateId, ticket }) {
    commit(MutationsType.UPDATE_TICKETS_BY_STATE, { state_id: stateId, ticket });
  },
  async getTicketById({ commit, state }, payload) {
    try {
      if (state.unsubscribeGetTicket) {
        state.unsubscribeGetTicket();
        commit(MutationsType.UNSUBSCRIBE_GET_TICKET, null);
      }

      commit(MutationsType.SET_TICKET, null);

      return await new Promise((resolve, reject) => {
        let firstSnapshot = true;
        const unsubscribe = ticketsCollection.doc(payload.ticket_id).onSnapshot(
          (doc) => {
            if (!doc.exists) {
              const error = new Error("La oportunidad solicitada no existe");
              if (firstSnapshot) reject(error);
              firstSnapshot = false;
              return;
            }

            const ticket = { ...doc.data(), id: doc.id };
            commit(MutationsType.SET_TICKET, ticket);

            if (state.tickets) {
              const existTicket = state.tickets.find((item) => item.id === payload.ticket_id);
              commit(MutationsType.UPDATE_TICKET, { id: doc.id, value: { ...existTicket, ...ticket } });
            }

            if (firstSnapshot) resolve(ticket);
            firstSnapshot = false;
          },
          (error) => {
            if (firstSnapshot) reject(error);
            else console.error("getTicketById listener: ", error);
            firstSnapshot = false;
          },
        );

        commit(MutationsType.UNSUBSCRIBE_GET_TICKET, unsubscribe);
      });
    } catch (error) {
      console.error("getTicketById: ", error);
      throw error;
    }
  },
  stopTicketListener({ commit, state }) {
    if (state.unsubscribeGetTicket) state.unsubscribeGetTicket();
    commit(MutationsType.UNSUBSCRIBE_GET_TICKET, null);
  },
  async setGroupTickets({ commit }, payload) {
    commit(MutationsType.SET_TICKETS, payload.tickets_selected);
  },
  async getAllTickets({ commit }) {
    let agent = await getCurrentUser();
    let reference = ticketsCollection;

    if ([roles.ADVISOR, roles.ADVISOR_LEVEL2].includes(agent.role))
      reference = reference.where("agent_id", "==", agent.uid);

    if ([roles.SUPERVISOR, roles.ENTREPRENEUR].includes(agent.role))
      reference = reference.where("team_id", "==", agent.team_id);

    await reference.orderBy("created_at", "desc").onSnapshot(async (snap) => {
      let data;
      commit(MutationsType.SET_TICKETS, []);
      snap.docs.forEach((doc) => {
        data = doc.data();
        commit(MutationsType.ADD_TICKET, { id: doc.id, ...data });
      });
    });
  },
  async getAllNewTickets({ commit, rootState, state }, payload) {
    try {
      let agent = rootState.Agent.agent;
      let reference = ticketsCollection;

      if ([roles.ADVISOR, roles.ADVISOR_LEVEL2].includes(agent.role))
        reference = reference.where("agent_id", "==", agent.uid);

      if ([roles.SUPERVISOR, roles.ENTREPRENEUR].includes(agent.role))
        reference = reference.where("team_id", "==", agent.team_id);

      reference = reference.orderBy("created_at", "asc");

      if (payload.last_ticket) {
        const start = await ticketsCollection.doc(payload.last_ticket.id).get();
        reference = reference.startAfter(start);
      }

      const unsubscribe = await reference.onSnapshot(async (snapshot) => {
        snapshot.docChanges().forEach((change) => {
          if (change.type === "added") {
            let newTickets = [];

            snapshot.docs.forEach((doc) => {
              if (state.tickets.find((t) => t.id === doc.id)) return;

              newTickets.push({ ...doc.data(), id: doc.id });
            });

            commit(MutationsType.SET_NEW_TICKETS, newTickets);
          }

          if (change.type === "removed") {
            commit(MutationsType.REMOVE_TICKET, { id: change.doc.id });
          }
        });
      });

      commit(MutationsType.UNSUBSCRIBE_GET_ALL_NEW_TICKETS, unsubscribe);
    } catch (error) {
      console.error("getAllNewTickets: ", error);
    }
  },
  async unsubscribeGetAllNewTickets({ commit, state }) {
    if (state.unsubscribeAllNewTickets) state.unsubscribeAllNewTickets();

    commit(MutationsType.SET_NEW_TICKETS, null);
    commit(MutationsType.UNSUBSCRIBE_GET_ALL_NEW_TICKETS, null);
  },
  async getTicketsWithFilters({ commit }, payload) {
    let ref = ticketsCollection;
    let filters = payload.filters;
    let tickets = [];

    if (filters.origin) ref = ref.where("origin", "==", filters.origin);
    if (filters.tags && filters.tags.length > 0) ref = ref.where("tags", "array-contains-any", filters.tags);

    filters.date_start = `${filters.date_start} 00:00:00`;
    filters.date_end = `${filters.date_end} 23:59:00`;

    ref = ref
      .where("created_at", ">=", new Date(filters.date_start))
      .where("created_at", "<=", new Date(filters.date_end));

    let snap = await ref.orderBy("created_at", "desc").limit(10).get();
    let emails = new Set();
    let data;

    snap.forEach((doc) => {
      data = doc.data();

      if (data.email && !emails.has(data.email) && emails.size < 6) {
        emails.add(data.email);
        tickets.push({ id: doc.id, ...data });
      }
    });

    commit(MutationsType.SET_TICKETS, tickets);
    return tickets;
  },
  async setTicketState({ commit }, payload) {
    try {
      let response = await requestAxios({
        url: `/awy/api/v1/ticket/${payload.ticket_id}/state`,
        method: "PATCH",
        data: payload,
      });

      commit(MutationsType.UPDATE_TICKET, { id: payload.ticket_id, add_value: { ...response.data.data } });
    } catch (error) {
      throw error.response.data;
    }
  },
  async setTicketObjection({ commit }, payload) {
    try {
      let response = await requestAxios({
        url: `/awy/api/v1/ticket/${payload.ticket_id}/objection`,
        method: "PATCH",
        data: payload.data,
      });

      commit(MutationsType.UPDATE_TICKET, { id: payload.ticket_id, add_value: { ...response.data.data } });
    } catch (error) {
      throw error.response.data;
    }
  },
  async setTicketReadWaResponse({ commit }, payload) {
    try {
      let response = await requestAxios({
        url: `/awy/api/v1/ticket/${payload.ticket_id}/wa-response`,
        method: "PATCH",
      });

      commit(MutationsType.UPDATE_TICKET, { id: payload.ticket_id, add_value: { ...response.data.data } });
    } catch (error) {
      throw error.response.data;
    }
  },
  // eslint-disable-next-line
  async setTicketAgent({}, { id, agentId }) {
    try {
      let response = await requestAxios({
        url: `/awy/api/v1/ticket/${id}/agent`,
        method: "PATCH",
        data: {
          ticket_id: id,
          agent_id: agentId,
        },
      });
      return response.data;
    } catch (error) {
      throw error.response.data;
    }
  },
  async setTicketProducts({ commit }, { id, products, discount }) {
    try {
      let response = await requestAxios({
        url: `/awy/api/v1/ticket/${id}/products`,
        method: "PATCH",
        data: { products, discount },
      });
      commit(MutationsType.UPDATE_TICKET, { id: id, add_value: { ...response.data.data } });
      return response.data;
    } catch (error) {
      throw error.response.data;
    }
  },
  async setTicketContact({ commit }, { id, documentNumber, documentType }) {
    try {
      let response = await requestAxios({
        url: `/awy/api/v1/ticket/${id}/contact`,
        method: "PATCH",
        data: { document_number: documentNumber, document_type: documentType },
      });
      commit(MutationsType.UPDATE_TICKET, { id: id, add_value: { contact_id: response.data.data.id } });
      return response.data;
    } catch (error) {
      throw error.response.data;
    }
  },
  async setTicketBotStatus({ commit }, { id }) {
    try {
      let response = await requestAxios({
        url: `/awy/api/v1/ticket/${id}/bot-status`,
        method: "PATCH",
      });

      commit(MutationsType.UPDATE_TICKET, { id: id, add_value: { ...response.data.data } });
    } catch (error) {
      throw error.response.data;
    }
  },
  async setTicketOpened({ commit }, { id }) {
    try {
      let response = await requestAxios({
        url: `/awy/api/v1/ticket/${id}/opened`,
        method: "PATCH",
      });

      commit(MutationsType.UPDATE_TICKET, { id: id, add_value: { ...response.data.data } });
    } catch (error) {
      throw error.response.data;
    }
  },
  async convertToOrder({ commit }, { id, data }) {
    try {
      let response = await requestAxios({
        url: `/awy/api/v1/ticket/${id}/convert-to-order`,
        method: "POST",
        data: data,
      });
      commit(MutationsType.UPDATE_TICKET, { id: id, add_value: { state: response.data.data.ticket.state } });
      return response.data;
    } catch (error) {
      throw error.response.data;
    }
  },
  // eslint-disable-next-line
  async updateTicket({ commit }, payload) {
    try {
      let response = await requestAxios({
        url: `/awy/api/v1/ticket/${payload.ticket_id}`,
        method: "PATCH",
        data: payload.data,
      });
      return response.data;
    } catch (error) {
      throw error.response.data;
    }
  },
  // eslint-disable-next-line
  async searchTickets({ commit }, { filters, search, page, perPage = 40, sortBy }) {
    try {
      let response = await requestAxios({
        url: "/awy/api/v1/ticket/search",
        method: "POST",
        data: {
          filters,
          search,
          page,
          perPage,
          sortBy,
        },
      });
      return response.data;
    } catch (error) {
      throw error.response.data;
    }
  },
  // eslint-disable-next-line
  async downloadTickets({ commit }) {
    try {
      let response = await requestAxios({
        url: "/awy/api/v1/ticket/download",
        method: "GET",
        responseType: "blob",
      });
      return response.data;
    } catch (error) {
      throw error.response.data;
    }
  },
  // eslint-disable-next-line
  async exportTicketTemplate({ commit }) {
    try {
      let response = await requestAxios({
        url: "/awy/api/v1/ticket/export-template",
        method: "GET",
        responseType: "blob",
      });
      return response.data;
    } catch (error) {
      throw error.response.data;
    }
  },
  // eslint-disable-next-line
  async importTickets({}, payload) {
    try {
      let formdata = new FormData();
      formdata.append("file", payload.file);
      let response = await requestAxios({
        url: "/awy/api/v1/ticket/importer",
        method: "POST",
        data: formdata,
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    } catch (error) {
      throw error.response.data;
    }
  },
  async deleteTicket({ commit, state }, { id }) {
    try {
      await requestAxios({
        url: `/awy/api/v1/ticket/${id}`,
        method: "DELETE",
      });

      if (state.id === id) commit(MutationsType.SET_TICKET, null);
    } catch (error) {
      throw error.response.data;
    }
  },
  showTicketDetail({ commit }, payload) {
    commit(MutationsType.SHOW_TICKET_DETAIL, payload.ticketId);
    commit(MutationsType.SHOW_TICKET_DETAIL_SECTION, payload.section || "");
  },
  hideTicketDetail({ commit, state }) {
    commit(MutationsType.SET_TICKET, null);
    commit(MutationsType.HIDE_TICKET_DETAIL);
    commit(MutationsType.SHOW_TICKET_DETAIL_SECTION, "");

    if (state.unsubscribeGetTicket) {
      state.unsubscribeGetTicket();
      commit(MutationsType.UNSUBSCRIBE_GET_TICKET, null);
    }
  },
  async getAllTicketDownloads({ commit, rootState }) {
    const agent = rootState.Agent.agent;
    let reference = ticketsDownloadsCollection;

    if ([roles.ENTREPRENEUR].includes(agent.role)) reference = reference.where("team_id", "==", agent.team_id);

    if ([roles.SUPERVISOR, roles.ADVISOR, roles.ADVISOR_LEVEL2].includes(agent.role))
      reference = reference.where("created_uid", "==", agent.uid);

    commit(MutationsType.SET_TICKET_DOWNLOADS, []);

    await reference.orderBy("created_at", "asc").onSnapshot(async (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === "added")
          commit(MutationsType.ADD_TICKET_DOWNLOAD, { ...change.doc.data(), id: change.doc.id });

        if (change.type === "modified")
          commit(MutationsType.UPDATE_TICKET_DOWNLOAD, { id: change.doc.id, data: change.doc.data() });

        if (change.type === "removed") commit(MutationsType.REMOVE_TICKET_DOWNLOAD, { id: change.doc.id });
      });
    });
  },
  // eslint-disable-next-line
  async setTicketDownload({}, payload) {
    try {
      let response = await requestAxios({
        url: "/awy/api/v1/ticket/downloads",
        method: "POST",
        data: payload,
      });
      return response.data;
    } catch (error) {
      throw error.response.data;
    }
  },
  // eslint-disable-next-line
  async addTicketComment({}, { id, comment }) {
    try {
      await requestAxios({
        url: `/awy/api/v1/ticket/${id}/comments`,
        method: "POST",
        data: { comment },
      });
    } catch (error) {
      throw error.response.data;
    }
  },
  // eslint-disable-next-line
  async addTicketCommentMedia({}, { id, comment, file }) {
    try {
      let formdata = new FormData();
      formdata.append("file", file);

      await requestAxios({
        url: `/awy/api/v1/ticket/${id}/comments/media`,
        method: "POST",
        data: formdata,
        headers: { "Content-Type": "multipart/form-data" },
        params: comment,
      });
    } catch (error) {
      throw error.response.data;
    }
  },
  // eslint-disable-next-line
  async downloadHistory({ commit }, { teamId }) {
    try {
      let response = await requestAxios({
        url: "/awy/api/v1/ticket/history/download",
        method: "GET",
        params: { team_id: teamId },
        responseType: "blob",
      });
      return response.data;
    } catch (error) {
      throw error.response.data;
    }
  },
  resetModuleTicket({ commit }) {
    commit(MutationsType.SET_TICKET, null);
    commit(MutationsType.SET_TICKETS, null);
    commit(MutationsType.SET_NEW_TICKETS, null);
    commit(MutationsType.SET_TICKET, null);
    commit(MutationsType.SET_TICKET_DOWNLOADS, null);
  },
  // eslint-disable-next-line
  async getTicketAllDetail({}, payload) {
    try {
      const doc = await ticketsCollection.doc(payload.id).get();

      return { id: doc.id, ...doc.data() };
    } catch (error) {
      console.error("getTicketById: ", error);
    }
  },
  // eslint-disable-next-line
  async getAllTicketAssistantLogs({}, { id, input }) {
    try {
      let response = await requestAxios({
        url: "/awy/api/v1/assistant-logs",
        method: "GET",
        params: {
          ticket_id: id,
          input,
        },
      });
      return response.data;
    } catch (error) {
      throw error.response.data;
    }
  },
};
